<template>
  <dialog
    ref="dialogEl"
    class="scrim"
    aria-labelledby="changelog-dialog-title"
    @cancel.prevent="emit('close')"
    @click="onScrimClick"
  >
    <div class="panel">
      <div class="head">
        <div>
          <p class="kicker">{{ t('changelog.kicker') }}</p>
          <h2 id="changelog-dialog-title">{{ t('changelog.title') }}</h2>
          <p class="lead">{{ t('changelog.dialogLead') }}</p>
        </div>
        <CloseXButton :label="t('changelog.dismiss')" @click="emit('close')" />
      </div>
      <div class="body">
        <ChangelogNotes :releases="releases" dense />
      </div>
      <div class="actions">
        <button type="button" class="ghost snooze" @click="emit('snooze')">
          {{ t('dialog.snoozeToday') }}
        </button>
        <button type="button" class="ghost" @click="emit('close')">
          {{ t('changelog.dismiss') }}
        </button>
        <router-link to="/changelog" class="cta" @click="emit('close')">
          {{ t('changelog.open') }}
        </router-link>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import type { ChangelogRelease } from '../i18n/changelog'
import { useI18n } from '../i18n/t'
import ChangelogNotes from './ChangelogNotes.vue'
import CloseXButton from './CloseXButton.vue'

defineProps<{
  releases: ChangelogRelease[]
}>()

const emit = defineEmits<{ close: []; snooze: [] }>()
const { t } = useI18n()
const dialogEl = ref<HTMLDialogElement | null>(null)

onMounted(() => {
  dialogEl.value?.showModal()
})

function onScrimClick(event: MouseEvent): void {
  if (event.target === dialogEl.value) emit('close')
}
</script>

<style scoped>
.scrim {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  padding: max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  border: 0;
  background: transparent;
  color: var(--md-sys-color-on-surface);
  overflow: hidden;
  animation: changelog-in var(--md-motion-medium) var(--md-motion-standard);
}

.scrim::backdrop {
  background: var(--md-scrim);
  backdrop-filter: blur(10px);
}

.panel {
  display: flex;
  flex-direction: column;
  width: min(560px, 100%);
  max-height: min(72vh, 640px);
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  border: 1px solid color-mix(in srgb, var(--md-sys-color-primary) 40%, transparent);
  box-shadow: var(--md-elevation-2);
  color: var(--md-sys-color-on-surface);
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 16px 12px 24px;
}

.kicker {
  margin: 0 0 4px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  font-size: 28px;
  font-weight: 400;
  line-height: 36px;
}

.lead {
  margin: 6px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.body {
  overflow: auto;
  overscroll-behavior: contain;
  padding: 4px 24px 8px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px 16px;
}

.snooze {
  margin-right: auto;
}

.cta,
.ghost {
  min-height: 40px;
  padding: 8px 16px;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
}

.cta {
  display: inline-flex;
  align-items: center;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.ghost {
  border: 0;
  background: transparent;
  color: var(--md-sys-color-primary);
}

.ghost:hover,
.cta:hover {
  filter: brightness(1.06);
}

@keyframes changelog-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .scrim {
    animation: none;
  }

  .scrim::backdrop {
    backdrop-filter: none;
  }
}
</style>
